import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatSAR, formatDate, statusColor } from "@/lib/utils";
import { toast } from "sonner";
import { Bus, Users, Wallet, ClipboardList, Truck, Building2, Plus, Trash2, Sparkles, Camera } from "lucide-react";
import TodaySchedule from "@/components/TodaySchedule";

function StatCard({ label, value, icon: Icon, color = "emerald" }) {
  const colors = { emerald: "bg-emerald-50 text-emerald-700", sky: "bg-sky-50 text-sky-700", amber: "bg-amber-50 text-amber-700", violet: "bg-violet-50 text-violet-700", rose: "bg-rose-50 text-rose-700" };
  return (
    <div className="abdan-card p-5">
      <div className="flex items-center justify-between">
        <div><div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div><div className="text-2xl font-bold mt-1">{value}</div></div>
        <div className={`w-12 h-12 rounded-xl grid place-items-center ${colors[color]}`}><Icon className="w-5 h-5" /></div>
      </div>
    </div>
  );
}

export function AdminDashboard() {
  const [summary, setSummary] = useState({});
  const [bookings, setBookings] = useState([]);
  useEffect(() => {
    api.get("/reports/summary").then((r) => setSummary(r.data));
    api.get("/bookings").then((r) => setBookings(r.data.items));
  }, []);
  return (
    <div className="space-y-6">
      <div><h1 className="text-3xl font-bold">Command Center</h1><p className="text-slate-500">Real-time operations overview.</p></div>
      <div className="grid md:grid-cols-4 gap-4">
        <StatCard label="Bookings" value={summary.total_bookings || 0} icon={ClipboardList} color="emerald" />
        <StatCard label="Revenue" value={formatSAR(summary.revenue || 0)} icon={Wallet} color="violet" />
        <StatCard label="Fleet" value={summary.fleet_count || 0} icon={Bus} color="sky" />
        <StatCard label="Customers" value={summary.customers_count || 0} icon={Users} color="amber" />
      </div>
      <div className="grid md:grid-cols-4 gap-4">
        <StatCard label="Drivers" value={summary.drivers_count || 0} icon={Truck} color="sky" />
        <StatCard label="Completed" value={summary.completed || 0} icon={ClipboardList} color="emerald" />
        <StatCard label="Pending" value={summary.pending || 0} icon={ClipboardList} color="amber" />
        <StatCard label="Utilization" value={`${Math.min(100, Math.round(((summary.total_bookings || 0) / Math.max(1, summary.fleet_count || 1)) * 5))}%`} icon={Sparkles} color="violet" />
      </div>
      <TodaySchedule />
      <Card><CardHeader><CardTitle>Recent Bookings</CardTitle></CardHeader>
        <CardContent><SimpleTable rows={bookings.slice(0, 8)} /></CardContent></Card>
    </div>
  );
}

function SimpleTable({ rows }) {
  return (
    <Table>
      <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Customer</TableHead><TableHead>Route</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead>Payment</TableHead><TableHead>Price</TableHead></TableRow></TableHeader>
      <TableBody>{rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-500">No data.</TableCell></TableRow>}
        {rows.map((b) => (<TableRow key={b.id}><TableCell className="font-mono font-semibold">{b.code}</TableCell><TableCell>{b.customer_name}</TableCell><TableCell>{b.origin} → {b.destination}</TableCell><TableCell className="text-sm">{formatDate(b.pickup_date)}</TableCell><TableCell><Badge className={statusColor(b.status)}>{b.status}</Badge></TableCell><TableCell><Badge className={statusColor(b.payment_status)}>{b.payment_status}</Badge></TableCell><TableCell className="font-semibold">{formatSAR(b.price)}</TableCell></TableRow>))}
      </TableBody>
    </Table>
  );
}

export function AdminBookings() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/bookings").then((r) => setRows(r.data.items)); }, []);
  return <div className="space-y-4"><h1 className="text-3xl font-bold">All Bookings</h1><Card><CardContent className="p-4"><SimpleTable rows={rows} /></CardContent></Card></div>;
}

export function AdminDispatch() {
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selVeh, setSelVeh] = useState({});
  const [selDrv, setSelDrv] = useState({});
  const load = () => { api.get("/bookings").then((r) => setBookings(r.data.items)); };
  useEffect(() => {
    load();
    api.get("/vehicles").then((r) => setVehicles(r.data.items));
    api.get("/drivers").then((r) => setDrivers(r.data.items));
  }, []);
  const assign = async (b) => {
    const vid = selVeh[b.id], did = selDrv[b.id];
    if (!vid || !did) return toast.error("Pick vehicle & driver");
    try { await api.patch(`/bookings/${b.id}/assign`, { vehicle_id: vid, driver_id: did }); toast.success("Assigned"); load(); }
    catch { toast.error("Assign failed"); }
  };
  const pending = bookings.filter((b) => ["pending", "confirmed"].includes(b.status));
  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold">Dispatch Board</h1>
      <div className="grid gap-4">
        {pending.length === 0 && <Card><CardContent className="p-8 text-center text-slate-500">Nothing to dispatch.</CardContent></Card>}
        {pending.map((b) => (
          <Card key={b.id} data-testid={`dispatch-${b.code}`}>
            <CardContent className="p-4 grid md:grid-cols-4 gap-3 items-end">
              <div><div className="text-xs text-slate-500 uppercase">Booking</div><div className="font-mono font-bold">{b.code}</div><div className="text-sm text-slate-600">{b.origin} → {b.destination}</div><Badge className={statusColor(b.status)}>{b.status}</Badge></div>
              <div><Label>Vehicle</Label>
                <Select onValueChange={(v) => setSelVeh({ ...selVeh, [b.id]: v })}>
                  <SelectTrigger data-testid={`sel-veh-${b.code}`}><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white">{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.plate} · {v.model}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Driver</Label>
                <Select onValueChange={(v) => setSelDrv({ ...selDrv, [b.id]: v })}>
                  <SelectTrigger data-testid={`sel-drv-${b.code}`}><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white">{drivers.map((d) => <SelectItem key={d.id} value={d.user_id}>{d.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => assign(b)} data-testid={`assign-${b.code}`}>Assign</Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export function AdminFleet() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ plate: "", model: "", type: "sedan", capacity: 4, year: 2024, image: "", status: "available", price_per_km: 4 });
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileRef = useRef();
  const load = () => api.get("/vehicles").then((r) => setRows(r.data.items));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.post("/vehicles", form); toast.success("Vehicle added"); setOpen(false); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/vehicles/${id}`); load(); } };
  const pickPhoto = (id) => { setUploading(id); fileRef.current.click(); };
  const uploadPhoto = async (file) => {
    if (!file || !uploading) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/vehicles/${uploading}/photo`, fd); toast.success("Foto armada diperbarui"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload gagal"); } finally { setUploading(null); }
  };
  return (
    <div className="space-y-4">
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { uploadPhoto(e.target.files[0]); e.target.value = ""; }} data-testid="veh-photo-input" />
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Fleet</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-add-vehicle"><Plus className="w-4 h-4 mr-1" />Add Vehicle</Button></DialogTrigger>
          <DialogContent className="bg-white"><DialogHeader><DialogTitle>Add Vehicle</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Plate</Label><Input value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} data-testid="veh-plate" /></div>
              <div><Label>Model</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="veh-model" /></div>
              <div><Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger data-testid="veh-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="sedan">Sedan</SelectItem><SelectItem value="staria">Staria</SelectItem><SelectItem value="gmc">GMC</SelectItem><SelectItem value="hiace">Hi-Ace</SelectItem><SelectItem value="coaster">Coaster</SelectItem><SelectItem value="bus">Bus</SelectItem></SelectContent></Select></div>
              <div><Label>Capacity</Label><Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: +e.target.value })} /></div>
              <div><Label>Year</Label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: +e.target.value })} /></div>
              <div><Label>Price/km (SAR)</Label><Input type="number" value={form.price_per_km} onChange={(e) => setForm({ ...form, price_per_km: +e.target.value })} /></div>
              <div className="col-span-2"><Label>Image URL</Label><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} /></div>
            </div>
            <Button className="bg-emerald-600 hover:bg-emerald-700 mt-3" onClick={save} data-testid="veh-save">Save</Button>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {rows.map((v) => (
          <div key={v.id} className="abdan-card overflow-hidden" data-testid={`vehicle-${v.plate}`}>
            <div className="h-40 bg-slate-100 relative group">
              {v.image && <img src={v.image} alt={v.model} className="w-full h-full object-cover" />}
              <Button size="sm" variant="secondary" className="absolute bottom-2 right-2 opacity-90" onClick={() => pickPhoto(v.id)} disabled={uploading === v.id} data-testid={`veh-photo-${v.plate}`}>
                <Camera className="w-4 h-4 mr-1" />{uploading === v.id ? "..." : "Foto"}</Button>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between mb-2"><h3 className="font-bold">{v.model}</h3><Badge className={statusColor(v.status)}>{v.status}</Badge></div>
              <div className="text-sm font-mono text-slate-500">{v.plate}</div>
              <div className="flex items-center justify-between mt-3 pt-2 border-t">
                <div className="text-xs text-slate-500">{v.capacity} pax · {v.type}</div>
                <Button size="sm" variant="ghost" onClick={() => del(v.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminDrivers() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", license_no: "", password: "Driver@123", status: "active" });
  const [open, setOpen] = useState(false);
  const load = () => api.get("/drivers").then((r) => setRows(r.data.items));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.post("/drivers", form); toast.success("Driver added"); setOpen(false); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/drivers/${id}`); load(); } };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Drivers</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-add-driver"><Plus className="w-4 h-4 mr-1" />Add Driver</Button></DialogTrigger>
          <DialogContent className="bg-white"><DialogHeader><DialogTitle>Add Driver</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="drv-name" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="drv-email" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>License No</Label><Input value={form.license_no} onChange={(e) => setForm({ ...form, license_no: e.target.value })} /></div>
              <div><Label>Password</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            </div>
            <Button className="bg-emerald-600 hover:bg-emerald-700 mt-3" onClick={save} data-testid="drv-save">Save</Button>
          </DialogContent>
        </Dialog>
      </div>
      <Card><CardContent className="p-4">
        <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>License</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>{rows.map((d) => (<TableRow key={d.id}><TableCell className="font-medium">{d.name}</TableCell><TableCell>{d.email}</TableCell><TableCell>{d.phone}</TableCell><TableCell className="font-mono">{d.license_no}</TableCell><TableCell><Badge className={statusColor(d.status)}>{d.status}</Badge></TableCell><TableCell><Button size="sm" variant="ghost" onClick={() => del(d.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button></TableCell></TableRow>))}</TableBody></Table>
      </CardContent></Card>
    </div>
  );
}

function UserList({ role, title }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/users?role=${role}`).then((r) => setRows(r.data.items)); }, [role]);
  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold">{title}</h1>
      <Card><CardContent className="p-4">
        <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Company</TableHead><TableHead>Joined</TableHead></TableRow></TableHeader>
          <TableBody>{rows.map((u) => (<TableRow key={u.id}><TableCell className="font-medium">{u.name}</TableCell><TableCell>{u.email}</TableCell><TableCell>{u.phone || "-"}</TableCell><TableCell>{u.company || "-"}</TableCell><TableCell className="text-sm">{formatDate(u.created_at)}</TableCell></TableRow>))}</TableBody></Table>
      </CardContent></Card>
    </div>
  );
}
export function AdminCustomers() { return <UserList role="customer" title="Customers" />; }
export function AdminPartners() { return <UserList role="partner" title="Partners" />; }

export function AdminFinance() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  useEffect(() => {
    api.get("/bookings").then((r) => setRows(r.data.items));
    api.get("/reports/summary").then((r) => setSummary(r.data));
  }, []);
  const paid = rows.filter((b) => b.payment_status === "paid");
  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold">Finance</h1>
      <div className="grid md:grid-cols-3 gap-4">
        <StatCard label="Total Revenue" value={formatSAR(summary.revenue || 0)} icon={Wallet} color="emerald" />
        <StatCard label="Paid Bookings" value={paid.length} icon={ClipboardList} color="sky" />
        <StatCard label="Pending Payment" value={rows.filter((b) => b.payment_status !== "paid").length} icon={ClipboardList} color="amber" />
      </div>
      <Card><CardHeader><CardTitle>Payment History</CardTitle></CardHeader><CardContent><SimpleTable rows={paid} /></CardContent></Card>
    </div>
  );
}

export function AdminReports() {
  const [summary, setSummary] = useState({});
  useEffect(() => { api.get("/reports/summary").then((r) => setSummary(r.data)); }, []);
  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold">Reports</h1>
      <div className="grid md:grid-cols-4 gap-4">
        <StatCard label="Bookings" value={summary.total_bookings || 0} icon={ClipboardList} color="emerald" />
        <StatCard label="Revenue" value={formatSAR(summary.revenue || 0)} icon={Wallet} color="violet" />
        <StatCard label="Fleet" value={summary.fleet_count || 0} icon={Bus} color="sky" />
        <StatCard label="Drivers" value={summary.drivers_count || 0} icon={Truck} color="amber" />
      </div>
      <Card><CardContent className="p-8 text-center text-slate-500">Detailed charts coming soon. Export CSV via /api/bookings.</CardContent></Card>
    </div>
  );
}

export function AdminCRM() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/bookings").then((r) => {
    const map = {};
    r.data.items.forEach((b) => { if (!map[b.customer_email]) map[b.customer_email] = { name: b.customer_name, email: b.customer_email, phone: b.customer_phone, count: 0, spend: 0, last: b.created_at };
      map[b.customer_email].count++; if (b.payment_status === "paid") map[b.customer_email].spend += b.price; });
    setRows(Object.values(map).sort((a, b) => b.spend - a.spend));
  }); }, []);
  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold">CRM · Top Customers</h1>
      <Card><CardContent className="p-4">
        <Table><TableHeader><TableRow><TableHead>Customer</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Bookings</TableHead><TableHead>Lifetime Value</TableHead></TableRow></TableHeader>
          <TableBody>{rows.map((c, i) => (<TableRow key={i}><TableCell className="font-medium">{c.name}</TableCell><TableCell>{c.email}</TableCell><TableCell>{c.phone}</TableCell><TableCell>{c.count}</TableCell><TableCell className="font-semibold text-emerald-700">{formatSAR(c.spend)}</TableCell></TableRow>))}</TableBody></Table>
      </CardContent></Card>
    </div>
  );
}

export function AdminSettings() {
  const { user } = useApp();
  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold">Settings</h1>
      <Card><CardContent className="p-6 space-y-3">
        <div><Label>Company</Label><Input defaultValue="Abdan Pro Transport" /></div>
        <div><Label>VAT No.</Label><Input defaultValue="300000000000003" /></div>
        <div><Label>Admin Email</Label><Input value={user.email} disabled /></div>
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => toast.success("Saved")}>Save</Button>
      </CardContent></Card>
    </div>
  );
}
